import React, {useCallback, useEffect, useState} from "react";
import {renderMarkdown} from "../../markdown/markdown-factory";
import {toast} from "react-toastify";

import {Button, Col, Row} from "react-bootstrap";
import ObjectEditor from "./ObjectEditor";
import TemplateEditor from "./TemplateEditor";
import ExampleSelector from "./ExamplesPanel";

import "./styles.css";
import CopyToClipboardButton from "../CopyToClipboardButton";
import {markMatchingText, prepareMarkdown} from "../../markdown/utils";

const Sandbox = ({
  key = "",
  samples = [],
  overrides = {},
  additionalSamples = [],
}) => {
  return ({fetchEntities, patchEntity}) => {
    const interpolate= key !== "";
    const defaultTemplate = samples.length > 0 ? samples[0].template : "";
    const defaultEntity = samples.length > 0 ? samples[0].dataObject : {};
    const [template, setTemplate] = useState(localStorage[`${key}-template`] || defaultTemplate);
    const [textFilter, setTextFilter] = useState("");
    const [editorText, setEditorText] = useState(localStorage[`${key}-entity`] || "");
    const [entityID, setEntityID] = useState(interpolate ? localStorage[`${key}-entity-id`] || "" : "");
    const [entity, setEntity] = useState(interpolate ? defaultEntity : null);
    const [rendered, setRendered] = useState(null);
    const [renderedAfterPrepare, setRenderedAfterPrepare] = useState(null);

    const markText = textFilter ? (input, params) => {
      return markMatchingText(input, textFilter, params);
    } : null;

    useEffect(() => {
      if (editorText) {
        localStorage[`${key}-entity`] = editorText;
        try {
          const parsed = JSON.parse(editorText);
          if (!_.isEqual(parsed, entity)) {
            console.debug("editor text changed, update entity", editorText);
            setEntity(parsed);
          }
        } catch (error) {
        }
      }
    }, [editorText]);

    useEffect(() => {
      if (_.isEqual(entity, {})) return;
      try {
        const serialized= JSON.stringify(entity, null, 2);
        if (serialized !== JSON.stringify(JSON.parse(editorText), null, 2)) {
          setEditorText(serialized);
        }
      }
      catch(error) {}
    }, [entity]);

    useEffect(() => {
      // Perform any additional interpolation if necessary
      const patched = patchEntity({...entity, _patched: false});
      setRendered(renderMarkdown({
                                   markdown: template,
                                   entity: patched,
                                   markText,
                                   overrides,
                                   prepare: false,
                                 }));
      setRenderedAfterPrepare(renderMarkdown({
                                               markdown: template,
                                               entity: patched,
                                               markText,
                                               overrides,
                                             }));
    }, [entity, template, markText]);

    const fetchEntity = (id) => {
      fetchEntities([id])
        .then(list => {
          if (list && list[0]) {
            setEntity(list[0]);
          }
        })
        .catch(error => toast.error(`Could not fetch data for ${id} (${error})`));
    };

    const reset = () => {
      setEntity(defaultEntity);
      setTemplate(defaultTemplate);
      setTextFilter("");
      delete localStorage[`${key}-template`];
      delete localStorage[`${key}-entity`];
      delete localStorage[`${key}-entity-id`];
    };
    const parametersChanged = selectedExample => {
      console.log(`params changed ${selectedExample}`);
      setEntity(selectedExample.dataObject);
      setTemplate(selectedExample.template);
      setTextFilter("");
    };

    return (
      <div className={"sandbox"}>
        <Row className="clear-margin">
          <Col md={key ? 4 : 6}>
            <h4>Template</h4>
            <TemplateEditor
              initText={template}
              onTemplateChange={(x) => {
                setTemplate(x);
                localStorage[`${key}-template`] = x;
              }}
            />
            <h4>After prep</h4>
            <textarea
              className="template-modified"
              readOnly
              value={prepareMarkdown(template)}
            />
          </Col>
          {interpolate && (<Col md={key ? 4 : 6}>
            <ObjectEditor
              key={template}
              data={editorText || ""}
              onChange={editorContent => setEditorText(editorContent.text)}
              onError={() => {}}
            />
          </Col>)}
          <Col md={key ? 4 : 6} className="rendered">
            <h4>Compiled w/o prep</h4>
            <div className="result-wrapper result-output result-default">{rendered}</div>
            <h4>Compiled w/prep</h4>
            <div className="result-wrapper result-output result-prepared">{renderedAfterPrepare}</div>
          </Col>
        </Row>
        <Row className="clear-margin">
          <Col md={12}>
            <div className="footer">
              <span className="example-title">Loaded Example:</span>
              <ExampleSelector
                exampleList={samples}
                additionalList={additionalSamples}
                onSelectionChange={parametersChanged}
              />
              <label className="example-title">
                Mark text <input type="text" value={textFilter} className="input-element" onChange={(e) => setTextFilter(e.target.value)}/>
              </label>
              <label>
                <button
                  className="btn btn-primary"
                  disabled={!entityID || entityID.indexOf(":") === -1}
                  onClick={() => fetchEntity(entityID)}>
                  Load by ID
                </button>
                <input type={"text"} value={entityID} onChange={(e) => {
                  setEntityID(e.target.value);
                  localStorage[`${key}-entity-id`] = e.target.value;
                }} className={"input-element"} onPaste={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const pastedText = e.clipboardData.getData("text/plain").trim();
                  console.log(`paste ${pastedText} => ${decodeURIComponent(pastedText)}`);
                  setEntityID(decodeURIComponent(pastedText));
                }}/>
              </label>
              <CopyToClipboardButton className={"float-right btn btn-primary"} getText={() => template}/>
              <Button className="float-right" onClick={reset}>Reset</Button>
            </div>
          </Col>
        </Row>
      </div>
    );
  }
};

export default Sandbox;
